import React from 'react'
import { useLangContext } from '../context/lang.context'
import { s, colors } from '../style'
import SubAgreement from './SubAgreement'

const Agreement = ({
  id,
  setFormAgreements,
  formAgreements,
  text,
  wasSent,
  isGroup,
  subAgreements,
  onAgreementsChange,
  data,
  formType
}) => {
  const { lang } = useLangContext()

  const toggleSubAgreement = (subIndex, optionIndex = null) => {
    setFormAgreements(prevAgreements => {
      const newAgreements = [...prevAgreements]
      
      if (!newAgreements[id]) {
        newAgreements[id] = {
          subAgreements: subAgreements.map(sub => {
            if (sub.options) {
              return {
                values: Array(sub.options.length).fill(false)
              }
            }
            return false
          })
        }
      }

      if (!newAgreements[id].subAgreements) {
        newAgreements[id].subAgreements = subAgreements.map(sub => {
          if (sub.options) {
            return {
              values: Array(sub.options.length).fill(false)
            }
          }
          return false
        })
      }
      
      if (optionIndex !== null) {
        if (!newAgreements[id].subAgreements[subIndex]) {
          newAgreements[id].subAgreements[subIndex] = {
            values: Array(subAgreements[subIndex].options.length).fill(false)
          }
        }
        
        const currentValues = [...newAgreements[id].subAgreements[subIndex].values]
        currentValues[optionIndex] = !currentValues[optionIndex]
        newAgreements[id].subAgreements[subIndex].values = currentValues

        const allOptionsChecked = newAgreements[id].subAgreements.every((sub, idx) => {
          if (idx === 0) return true
          if (idx === 1) return sub === true
          if (typeof sub === 'object' && sub.values) {
            return sub.values.every(value => value === true)
          }
          return true
        })
        newAgreements[id].subAgreements[0] = allOptionsChecked
      } else {
        if (subAgreements[subIndex].isCheckAll) {
          const newValue = !newAgreements[id].subAgreements[subIndex]
          
          newAgreements[id].subAgreements = newAgreements[id].subAgreements.map((sub, idx) => {
            if (idx <= 1) return newValue
            if (typeof sub === 'object' && sub.values) {
              return {
                values: Array(sub.values.length).fill(newValue)
              }
            }
            return sub
          })
        } else {
          newAgreements[id].subAgreements[subIndex] = 
            !newAgreements[id].subAgreements[subIndex]

          if (!subAgreements[subIndex].isCheckAll) {
            const allChecked = newAgreements[id].subAgreements.every((sub, idx) => {
              if (idx === 0) return true
              if (idx === 1) return sub === true
              if (typeof sub === 'object' && sub.values) {
                return sub.values.every(value => value === true)
              }
              return true
            })
            newAgreements[id].subAgreements[0] = allChecked
          }
        }
      }

      if (onAgreementsChange && data) {
        const formattedAgreements = newAgreements.reduce((acc, agreement, index) => {
          if (data[index]?.showFor?.includes(formType)) {
            const groupName = data[index].groupName || `group${index + 1}`
            
            return {
              ...acc,
              [groupName]: {
                consent: agreement.subAgreements[1],
                email: agreement.subAgreements[2]?.values?.[0] || false,
                phone: agreement.subAgreements[2]?.values?.[1] || false
              }
            }
          }
          return acc
        }, {})

        onAgreementsChange(formattedAgreements)
      }

      return newAgreements
    })
  }

  return (
    <div css={sAgreement}>
      <div css={sAgreementMain}>
        <div className='form_agreement_text'>
          {typeof text === 'string' ? text : text.__html}
        </div>
      </div>

      {isGroup && (
        <div css={sSubAgreements}>
          {subAgreements.map((sub, subIndex) => {
            if (sub.options) {
              return (
                <div key={subIndex}>
                  <SubAgreement
                    text={sub.label[lang]}
                    isLabel={true}
                  />
                  <div css={sSubAgreementOptions}>
                    {sub.options.map((option, optIndex) => (
                      <SubAgreement
                        key={optIndex}
                        text={option[lang]}
                        checked={formAgreements?.[id]?.subAgreements?.[subIndex]?.values?.[optIndex] ?? false}
                        onChange={() => toggleSubAgreement(subIndex, optIndex)}
                      />
                    ))}
                  </div>
                </div>
              )
            }
            return (
              <SubAgreement
                key={subIndex}
                text={sub[lang]}
                checked={formAgreements?.[id]?.subAgreements?.[subIndex] ?? false}
                onChange={() => toggleSubAgreement(subIndex)}
                required={sub.isRequired}
                wasSent={wasSent}
                pulseError={sub.isCheckAll && wasSent && subAgreements.some(s => s.isRequired && !formAgreements?.[id]?.subAgreements?.[1])}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

const sAgreement = {
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '20px',
  [s.xs]: { width: '100%' },
  '.form_agreement_check': {
    flexShrink: 0,
    transition: 'box-shadow 0.25s ease-out, opacity 0.25s ease-out',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 20,
    height: 20,
    boxShadow: `0px 0px 0px 2px ${colors.greyDarker}`,
    opacity: 0.3,
    marginRight: '1rem',
    borderRadius: 5,
    '&:focus': {
      boxShadow: `0px 0px 0px 4px ${colors.greyDarker}`,
    },
    '&.checked': {
      boxShadow: `0px 0px 0px 2px ${colors.brand}`,
      opacity: 1,
    },
    '&.pulse-error': {
      animation: 'pulseError 2s infinite',
      opacity: 1,
    }
  },
  '.form_agreement_check_inside': {
    transition: 'transform 0.25s ease-in-out, height 0.25s ease-out',
    width: 14,
    height: 14,
    borderRadius: 4,
    backgroundColor: colors.brand,
    transform: 'scale(0)',
  },
  '.form_agreement_text': {
    transition: 'color 0.25s ease-out, opacity 0.25s ease-out',
    opacity: 0.6,
    fontSize: 11,
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
    [s.md]: {
      paddingRight: '1rem',
    },
  },
  '@keyframes pulseError': {
    '0%': {
      boxShadow: `0px 0px 0px 2px ${colors.brand}`,
    },
    '50%': {
      boxShadow: `0px 0px 0px 4px ${colors.brand}`,
    },
    '100%': {
      boxShadow: `0px 0px 0px 2px ${colors.brand}`,
    }
  },
}

const sSubAgreements = {
  marginLeft: '10px',
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  paddingTop: '10px',
}

const sSubAgreementOptions = {
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'nowrap',
  gap: '8px',
  paddingTop: '8px',
  maxWidth: '430px',
}

const sAgreementMain = {
  display: 'flex',
  alignItems: 'center',
  width: '100%',
}

export default Agreement